import { t } from "i18next";
import { ReactNode } from "react";

import Active from "@/pages/Integrations/icons/Active";
import AppSell from "@/pages/Integrations/icons/AppSell";
import Astron from "@/pages/Integrations/icons/Astron";
import Bot from "@/pages/Integrations/icons/Bot";
import Cademi from "@/pages/Integrations/icons/Cademi";
import Eagle from "@/pages/Integrations/icons/EagleLabs";
import JetpackIcon from "@/pages/Integrations/icons/Jetpack";
import Enotas from "@/pages/Integrations/icons/Enotas";
import Hotzap from "@/pages/Integrations/icons/Hotzap";
import Memberkit from "@/pages/Integrations/icons/Memberkit";
import Mex from "@/pages/Integrations/icons/Mex";
import Nemu from "@/pages/Integrations/icons/Nemu";
import Notaz from "@/pages/Integrations/icons/Notaz";
import RedTracky from "@/pages/Integrations/icons/RedTracky";
import RedRock from "@/pages/Integrations/icons/RedRock";
import ShipOffers from "@/pages/Integrations/icons/ShipOffers";
import Sms from "@/pages/Integrations/icons/Sms";
import Tb3 from "@/pages/Integrations/icons/Tb3";
import TaukIcon from "@/pages/Integrations/icons/Tauk";
import UtmKit from "@/pages/Integrations/icons/UtmKit";
import Voxuy from "@/pages/Integrations/icons/Voxuy";
import Vturb from "@/pages/Integrations/icons/Vturb";
import Webhook from "@/pages/Integrations/icons/Webhook";
import Xtracky from "@/pages/Integrations/icons/Xtracky";
import HelpGridIcon from "@/pages/Integrations/icons/HelpGrid";

export type IntegrationCategory =
  | "memberArea"
  | "tracking"
  | "automations"
  | "vsl"
  | "sms"
  | "globalEncapsulation"
  | "invoices"
  | "webhooks";

export interface Integration {
  id: number;
  name: string;
  icon: ReactNode;
  description: string;
  disabled: boolean;
  route: string;
  category: IntegrationCategory;
  apiIdentifier?: string;
}

// Todas as integrações disponíveis
export const getAllIntegrations = (): Integration[] => [
  // Área de Membros
  {
    id: 6,
    name: "Memberkit",
    icon: <Memberkit />,
    description: t("integration:memberkitDescription"),
    disabled: false,
    route: "/memberkit",
    category: "memberArea",
  },
  {
    id: 1,
    name: "Cademi",
    icon: <Cademi />,
    description: t("integration:cademiDescription"),
    disabled: false,
    route: "/cademi",
    category: "memberArea",
  },
  {
    id: 9,
    name: "Astron Members",
    icon: <Astron />,
    description: t("integration:astronDescription"),
    disabled: false,
    route: "/astron_members",
    category: "memberArea",
  },
  {
    id: 13,
    name: "App Sell",
    icon: <AppSell />,
    description: t("integration:appSellDescription"),
    disabled: false,
    route: "/app_sell",
    category: "memberArea",
  },
  // Tracking
  {
    id: 2,
    name: "UTMify",
    icon: <UtmKit />,
    description: t("integration:utmifyDescription"),
    disabled: false,
    route: "/utmify",
    category: "tracking",
    apiIdentifier: "utmfy",
  },
  {
    id: 3,
    name: "Nemu",
    icon: <Nemu />,
    description: t("integration:nemuDescription"),
    disabled: true,
    route: "/nemu",
    category: "tracking",
  },
  {
    id: 8,
    name: "xTracky",
    icon: <Xtracky />,
    description: t("integration:xtrackyDescription"),
    disabled: false,
    route: "/xtracky",
    category: "tracking",
  },
  {
    id: 14,
    name: "RedTrack",
    icon: <RedTracky />,
    description: t("integration:redtrackDescription"),
    disabled: true,
    route: "/redtrack",
    category: "tracking",
  },
  // Automations
  {
    id: 15,
    name: "ActiveCampaign",
    icon: <Active />,
    description: t("integration:activeCampaignDescription"),
    disabled: false,
    route: "/active_campaign",
    category: "automations",
  },
  {
    id: 22,
    name: "Tauk",
    icon: <TaukIcon />,
    description: t("integration:taukDescription"),
    disabled: false,
    route: "/tauk",
    category: "automations",
  },
  {
    id: 10,
    name: "Hotzapp",
    icon: <Hotzap />,
    description: t("integration:hotzappDescription"),
    disabled: false,
    route: "/hotzapp",
    category: "automations",
  },
  {
    id: 16,
    name: "Voxuy",
    icon: <Voxuy />,
    description: t("integration:voxuyDescription"),
    disabled: true,
    route: "/voxuy",
    category: "automations",
  },
  {
    id: 5,
    name: "BotConversa",
    icon: <Bot />,
    description: t("integration:botConversaDescription"),
    disabled: false,
    route: "/bot_conversa",
    category: "automations",
  },
  // VSL
  {
    id: 30,
    name: "VTurb",
    icon: <Vturb />,
    description: t("integration:vturbDescription"),
    disabled: false,
    route: "/vturb",
    category: "vsl",
  },
  // SMS
  {
    id: 7,
    name: "SMS Funnel",
    icon: <Sms />,
    description: t("integration:smsFunnelDescription"),
    disabled: false,
    route: "/sms_funnel",
    category: "sms",
  },
  {
    id: 17,
    name: "Mex10",
    icon: <Mex />,
    description: t("integration:mex10Description"),
    disabled: true,
    route: "/mex10",
    category: "sms",
  },
  // Global Encapsulation
  {
    id: 11,
    name: "Eagle Labs",
    icon: <Eagle />,
    description: t("integration:eagleLabsDescription"),
    disabled: false,
    route: "/eagle_labs",
    category: "globalEncapsulation",
  },
  {
    id: 21,
    name: "Jetpack (JetStream)",
    icon: <JetpackIcon />,
    description: t("integration:jetpackDescription"),
    disabled: false,
    route: "/jetpack",
    category: "globalEncapsulation",
  },
  {
    id: 18,
    name: "ShipOffers",
    icon: <ShipOffers />,
    description: t("integration:shipOffersDescription"),
    disabled: false,
    route: "/ship-offers",
    category: "globalEncapsulation",
    apiIdentifier: "ship_offers",
  },
  {
    id: 34,
    name: "RedRock",
    icon: <RedRock />,
    description: t("integration:redRockDescription"),
    disabled: false,
    route: "/red_rock",
    category: "globalEncapsulation",
  },
  {
    id: 4,
    name: "Tb3",
    icon: <Tb3 />,
    description: t("integration:tb3Description"),
    disabled: false,
    route: "/tb3",
    category: "globalEncapsulation",
  },
  // Invoices
  {
    id: 19,
    name: "Notazz",
    icon: <Notaz />,
    description: t("integration:notazzDescription"),
    disabled: false,
    route: "/notazz",
    category: "invoices",
  },
  {
    id: 20,
    name: "eNotas",
    icon: <Enotas />,
    description: t("integration:enotasDescription"),
    disabled: false,
    route: "/enotas",
    category: "invoices",
  },
  // Webhooks
  {
    id: 12,
    name: "Webhook Genérico",
    icon: <Webhook />,
    description: t("integration:webhookDescription"),
    disabled: false,
    route: "/webhook",
    category: "webhooks",
  },
  {
    id: 33,
    name: "HelpGrid",
    icon: <HelpGridIcon />,
    description: t("integration:helpgridDescription"),
    disabled: false,
    route: "/helpgrid",
    category: "automations",
  },
];

export const getActiveIntegrations = () =>
  getAllIntegrations().filter((integration) => !integration.disabled);

export const getIntegrationsByCategory = (category: IntegrationCategory) =>
  getAllIntegrations().filter(
    (integration) => integration.category === category,
  );

export const getIntegrationsForSidebar = () =>
  getActiveIntegrations().map((integration) => ({
    title: integration.name,
    url: `/integrations${integration.route}`,
    icon: integration.icon,
    disabled: integration.disabled,
  }));

// Helper para obter integrações formatadas para filtro do webhook history
export const getIntegrationsForWebhookFilter = () => {
  const webhookIntegrations = [
    "astron_members",
    "cademi",
    "app_sell",
    "bot_conversa",
    "eagle_labs",
    "jetpack",
    "enotas",
    "hotzapp",
    "memberkit",
    "notazz",
    "sms_funnel",
    "tb3",
    "utmfy",
    "vturb",
    "webhook",
    "xtracky",
    "tauk",
    "helpgrid",
    "ship_offers",
    "red_rock",
  ];

  return getAllIntegrations()
    .filter((integration) => {
      const identifier = integration.apiIdentifier || integration.route.replace("/", "");
      return webhookIntegrations.includes(identifier);
    })
    .map((integration) => ({
      id: integration.id,
      label: integration.name,
      value: integration.apiIdentifier || integration.route.replace("/", ""),
    }));
};

export const getIntegrationWithIcon = (integrationValue: string) => {
  if (!integrationValue) return null;

  const integration = getAllIntegrations().find(
    (i) => (i.apiIdentifier || i.route.replace("/", "")) === integrationValue,
  );

  if (!integration) return null;

  return (
    <div className="flex items-center gap-2">
      {integration.icon}
      <span>{integration.name}</span>
    </div>
  );
};
